import { useContext, useEffect, useRef } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { SiteContext } from "src/context/site";
import { WorkspaceContext } from "src/context/workspace";
import { classNames } from "src/helpers/classNames";
import useDevice from "src/hooks/useDevice";
import useIframeResize from "src/hooks/useIframeResize";

export default function MetabaseEmbedProgrammaticFiltering({ content }) {
  const { details: workspaceDetails, menuType } = useContext(WorkspaceContext);
  const { site } = useContext(SiteContext);
  const { maxWidth } = useIframeResize({ workspaceDetails, site });
  const device = useDevice();
  const iframeWrapperRef = useRef(null);
  const isTokenExpired = useRef(false);

  const isExtension = window.location.protocol === "chrome-extension:";

  useEffect(() => {
    if (!content?.expiration_time) return;

    const checkExpiration = () => {
      const currentTime = Math.floor(Date.now() / 1000);
      isTokenExpired.current = currentTime >= content.expiration_time;
    };

    checkExpiration();

    const interval = setInterval(checkExpiration, 5000);

    return () => clearInterval(interval);
  }, [content?.expiration_time]);

  useEffect(() => {
    if (isExtension) {
      window.dispatchEvent(new CustomEvent("domo:iframe-loaded", {}));
      return;
    }

    const wrapper = iframeWrapperRef.current;
    const iframe = wrapper?.querySelector("iframe");
    if (!iframe) return;

    const handleLoad = () => {
      window.dispatchEvent(new CustomEvent("domo:iframe-loaded", {}));
    };

    const handleActivity = () => {
      if (isTokenExpired.current) {
        window.dispatchEvent(new CustomEvent("metabase:token-expired", {}));
      }
    };

    const handleWindowBlur = () => {
      const activeElement = document.activeElement;
      if (activeElement === iframe) {
        handleActivity();
      }
    };

    const handleMouseMove = () => {
      if (isTokenExpired.current) {
        handleActivity();
      }
    };

    iframe.addEventListener("load", handleLoad);
    window.addEventListener("blur", handleWindowBlur);
    wrapper.addEventListener("mousemove", handleMouseMove);

    return () => {
      iframe.removeEventListener("load", handleLoad);
      window.removeEventListener("blur", handleWindowBlur);
      wrapper.removeEventListener("mousemove", handleMouseMove);
    };
  }, [content.embedUrl, isExtension]);

  const handleOpenInNewTab = () => {
    if (content?.embedUrl) {
      window.open(content.embedUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={classNames("relative flex h-full w-full items-center justify-center", menuType === "horizontal" || menuType === "vertical" ? "" : "")}>
      {isExtension
        ? content?.embedUrl && (
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center gap-2 rounded-lg bg-highlightColor px-4 py-2 font-medium text-white shadow transition hover:opacity-80">
              Open Dashboard
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
            </button>
          )
        : content?.embedUrl && (
            <div
              ref={iframeWrapperRef}
              className="mx-auto h-full w-full overflow-auto"
              style={{
                maxWidth: device === "desktop" ? maxWidth || "100%" : "100%",
              }}>
              <iframe
                id="iframe"
                title="Metabase Embed"
                src={content.embedUrl}
                width="100%"
                height="100%"
              />
            </div>
          )}
    </div>
  );
}
